import React, { useState, useEffect } from 'react';
import { List, Plus, Edit2, Trash2, Eye, EyeOff, Save, X, AlertCircle, Sparkles } from 'lucide-react';
import { API_URL } from '../../config';
import { toast } from 'sonner';

export default function PriceListManagement({ adminToken }) {
  const [items, setItems] = useState([]);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch price list items
      const itemsRes = await fetch(`${API_URL}/api/pricing/admin/price-list`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(data.items || []);
      }
      
      // Fetch pricing settings (für SNAP Conversion Rate)
      const settingsRes = await fetch(`${API_URL}/api/pricing/admin/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setPricingSettings(data);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const createItem = async (itemData) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/price-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });

      if (res.ok) {
        toast.success('Item erstellt');
        setShowCreateForm(false);
        await fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const updateItem = async (itemId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/price-list/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        toast.success('Item aktualisiert');
        setEditingItem(null);
        await fetchData();
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteItem = async (itemId) => {
    if (!confirm('Item wirklich löschen?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/price-list/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (res.ok) {
        toast.success('Item gelöscht');
        await fetchData();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (itemId, currentStatus) => {
    await updateItem(itemId, { is_active: !currentStatus });
  };

  const initDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/price-list/init-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.created) {
          await fetchData();
        }
      } else {
        toast.error('Fehler beim Initialisieren');
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      toast.error('Fehler beim Initialisieren');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" style={{ overscrollBehavior: 'none' }}>
      
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
              <List className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Preisliste Editor</h1>
              <p className="text-white/60 text-sm">Editiere die öffentliche SNAP-Preisliste auf der Credits-Seite</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            {items.length === 0 && (
              <button
                onClick={initDefaults}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all flex items-center gap-2"
                data-testid="init-defaults-button"
              >
                <Sparkles className="w-4 h-4" />
                Defaults initialisieren
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-[#10B981] text-black rounded-lg hover:bg-[#059669] transition-all flex items-center gap-2 font-bold"
              data-testid="create-item-button"
            >
              <Plus className="w-4 h-4" />
              Neues Item
            </button>
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-white/80">
            <p className="font-bold mb-1">So funktioniert's:</p>
            <ul className="list-disc list-inside space-y-1 text-white/60">
              <li>Erstelle Items mit Icon, Text, SNAP-Preis und Farbe</li>
              <li>Sortierung per Drag & Drop oder manuell über "sort_order"</li>
              <li>Inaktive Items werden nicht auf der öffentlichen Seite angezeigt</li>
              <li>Die EUR-Preise werden automatisch berechnet (1 SNAP = €{((1 / (pricingSettings?.snaps_per_euro || 2))).toFixed(2)})</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-4 mb-8">
        {items.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-white/20 rounded-xl">
            <List className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <p className="text-white/50 mb-4">Noch keine Preislisten-Items vorhanden</p>
            <button
              onClick={initDefaults}
              className="px-6 py-3 bg-[#10B981] text-black rounded-lg hover:bg-[#059669] transition-all font-bold"
            >
              Defaults initialisieren
            </button>
          </div>
        ) : (
          items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              pricingSettings={pricingSettings}
              isEditing={editingItem === item.id}
              onEdit={() => setEditingItem(item.id)}
              onCancelEdit={() => setEditingItem(null)}
              onSave={(updates) => updateItem(item.id, updates)}
              onDelete={() => deleteItem(item.id)}
              onToggleActive={() => toggleActive(item.id, item.is_active)}
            />
          ))
        )}
      </div>

      {/* Live Preview */}
      <div className="mt-12 p-6 bg-black border-2 border-[#10B981]/30 rounded-2xl">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-[#10B981]" />
          Live-Vorschau (Öffentliche Ansicht)
        </h3>
        <PreviewSection items={items.filter(i => i.is_active)} pricingSettings={pricingSettings} />
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <CreateItemModal
          onClose={() => setShowCreateForm(false)}
          onSave={createItem}
          pricingSettings={pricingSettings}
        />
      )}

    </div>
  );
}


// ============================================================================
// ITEM CARD
// ============================================================================

function ItemCard({ item, pricingSettings, isEditing, onEdit, onCancelEdit, onSave, onDelete, onToggleActive }) {
  const [form, setForm] = useState(item);

  useEffect(() => {
    setForm(item);
  }, [item]);

  const handleSave = () => {
    const updates = {};
    Object.keys(form).forEach(key => {
      if (form[key] !== item[key] && !['id', 'created_at', 'updated_at', 'created_by', 'updated_by'].includes(key)) {
        updates[key] = form[key];
      }
    });

    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      onCancelEdit();
    }
  };

  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;
  const eurPrice = (form.snaps / snapsPerEuro).toFixed(2);

  return (
    <div className={`p-6 bg-black border-2 rounded-xl transition-all ${
      item.is_active ? 'border-[#10B981]/30' : 'border-white/10 opacity-60'
    }`} data-testid={`item-card-${item.id}`} style={{ overscrollBehavior: 'none' }}>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{form.icon}</span>
          <div>
            <h4 className="text-lg font-bold text-white">{form.label}</h4>
            {item.is_active ? (
              <span className="px-2 py-1 bg-[#10B981]/20 text-[#10B981] rounded text-xs font-bold">AKTIV</span>
            ) : (
              <span className="px-2 py-1 bg-white/10 text-white/40 rounded text-xs font-bold">INAKTIV</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={onToggleActive}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                title={item.is_active ? 'Deaktivieren' : 'Aktivieren'}
              >
                {item.is_active ? <Eye className="w-5 h-5 text-white/60" /> : <EyeOff className="w-5 h-5 text-white/40" />}
              </button>
              <button
                onClick={onEdit}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                title="Bearbeiten"
                data-testid={`edit-item-${item.id}`}
              >
                <Edit2 className="w-5 h-5 text-white/60" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                title="Löschen"
                data-testid={`delete-item-${item.id}`}
              >
                <Trash2 className="w-5 h-5 text-red-400" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="px-3 py-2 bg-[#10B981] text-black rounded-lg hover:bg-[#059669] transition-all flex items-center gap-1 font-bold text-sm"
                data-testid={`save-item-${item.id}`}
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Speichern</span>
              </button>
              <button
                onClick={onCancelEdit}
                className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm font-medium"
              >
                <span className="hidden sm:inline">Abbrechen</span>
                <X className="w-4 h-4 sm:hidden" />
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <ItemEditForm item={form} onChange={setForm} pricingSettings={pricingSettings} />
      ) : (
        <ItemView item={item} pricingSettings={pricingSettings} />
      )}

    </div>
  );
}


// ============================================================================
// ITEM VIEW (Read-only)
// ============================================================================

function ItemView({ item, pricingSettings }) {
  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;
  const eurPrice = (item.snaps / snapsPerEuro).toFixed(2);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      
      {/* Darstellung */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Darstellung</h5>
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{item.icon}</span>
            <div>
              <p className="text-white font-medium">{item.label}</p>
              {item.description && <p className="text-white/60 text-sm">{item.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/60">Farbe:</span>
            <div 
              className="w-6 h-6 rounded border-2 border-white/20"
              style={{ backgroundColor: item.color }}
            ></div>
            <span className="text-xs text-white/40">{item.color}</span>
          </div>
        </div>
      </div>

      {/* Preis */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Preis</h5>
        <div className="space-y-2">
          <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl">
            <p className="text-[#10B981] text-3xl font-bold mb-1">{item.snaps} SNAP{item.snaps !== 1 ? 's' : ''}</p>
            <p className="text-[#10B981]/60 text-sm">≈ €{eurPrice}</p>
          </div>
        </div>
      </div>

      {/* Sortierung */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Sortierung</h5>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">Position:</span>
            <span className="text-white font-bold text-lg">{item.sort_order}</span>
          </div>
          <p className="text-xs text-white/40">Niedriger = weiter oben</p>
        </div>
      </div>

    </div>
  );
}


// ============================================================================
// ITEM EDIT FORM
// ============================================================================

function ItemEditForm({ item, onChange, pricingSettings }) {
  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;
  const eurPrice = (item.snaps / snapsPerEuro).toFixed(2);

  const COLOR_PRESETS = [
    { value: '#10B981', label: 'Grün' },
    { value: '#3B82F6', label: 'Blau' },
    { value: '#A855F7', label: 'Purple' },
    { value: '#F97316', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' },
    { value: '#FBBF24', label: 'Gelb' }
  ];

  const ICON_PRESETS = ['🎓', '📸', '✨', '🎨', '🚀', '💎', '⭐', '🔥', '⚡', '🎯', '🏆', '🎁'];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      
      <div>
        <label className="text-xs text-white/60 block mb-2">Icon *</label>
        <div className="mb-2 flex flex-wrap gap-2">
          {ICON_PRESETS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => onChange({...item, icon: emoji})}
              className={`w-10 h-10 rounded-lg text-xl transition-all ${
                item.icon === emoji ? 'bg-[#10B981] scale-110' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {emoji}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={item.icon}
          onChange={(e) => onChange({...item, icon: e.target.value})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-2xl text-center focus:border-[#10B981] transition-all"
          placeholder="Oder eigenes Emoji eingeben..."
          data-testid="icon-input"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Label *</label>
        <input
          type="text"
          value={item.label}
          onChange={(e) => onChange({...item, label: e.target.value})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="z.B. Training (Avatar, Vehicle, etc.)"
          data-testid="label-input"
        />
      </div>

      <div className="col-span-full">
        <label className="text-xs text-white/60 block mb-2">Beschreibung (optional)</label>
        <input
          type="text"
          value={item.description || ''}
          onChange={(e) => onChange({...item, description: e.target.value})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="Optionale Zusatzinfo..."
          data-testid="description-input"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">SNAP-Preis * (beliebige Dezimalwerte)</label>
        <input
          type="number"
          step="0.01"
          min="0.01"
          inputMode="decimal"
          value={item.snaps}
          onChange={(e) => {
            const val = e.target.value.replace(',', '.');
            onChange({...item, snaps: parseFloat(val) || 0.01});
          }}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
          placeholder="1.0 oder 0.34"
          data-testid="snaps-input"
        />
        <p className="text-xs text-[#10B981] mt-1">= €{eurPrice}</p>
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Sortierung</label>
        <input
          type="number"
          value={item.sort_order}
          onChange={(e) => onChange({...item, sort_order: parseInt(e.target.value) || 0})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
          placeholder="0"
          data-testid="sort-order-input"
        />
        <p className="text-xs text-white/40 mt-1">Niedriger = weiter oben</p>
      </div>

      <div className="col-span-full">
        <label className="text-xs text-white/60 block mb-2">Farbe</label>
        <div className="flex gap-2">
          {COLOR_PRESETS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => onChange({...item, color: color.value})}
              className={`w-12 h-12 rounded-lg border-2 transition ${
                item.color === color.value ? 'border-white scale-110' : 'border-white/20'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.label}
            />
          ))}
        </div>
      </div>

    </div>
  );
}


// ============================================================================
// CREATE ITEM MODAL
// ============================================================================

function CreateItemModal({ onClose, onSave, pricingSettings }) {
  const [form, setForm] = useState({
    icon: '💎',
    label: 'Neues Item',
    description: '',
    snaps: 1.0,
    color: '#10B981',
    sort_order: 0,
    is_active: true
  });

  const handleSubmit = () => {
    if (!form.icon || !form.label || !form.snaps) {
      toast.error('Icon, Label und SNAP-Preis sind erforderlich');
      return;
    }
    if (form.snaps < 0.01) {
      toast.error('SNAP-Preis muss mindestens 0.01 sein');
      return;
    }
    onSave(form);
  };

  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;
  const eurPrice = (form.snaps / snapsPerEuro).toFixed(2);

  const COLOR_PRESETS = [
    { value: '#10B981', label: 'Grün' },
    { value: '#3B82F6', label: 'Blau' },
    { value: '#A855F7', label: 'Purple' },
    { value: '#F97316', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' },
    { value: '#FBBF24', label: 'Gelb' }
  ];

  const ICON_PRESETS = ['🎓', '📸', '✨', '🎨', '🚀', '💎', '⭐', '🔥', '⚡', '🎯', '🏆', '🎁'];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" style={{ overscrollBehavior: 'none' }}>
      <div className="bg-black border-2 border-[#10B981]/30 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
        
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Plus className="w-7 h-7 text-[#10B981]" />
            Neues Preislisten-Item
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="space-y-6">
          
          <div>
            <label className="text-sm font-bold text-white mb-2 block">Icon *</label>
            <div className="mb-3 flex flex-wrap gap-2">
              {ICON_PRESETS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setForm({...form, icon: emoji})}
                  className={`w-12 h-12 rounded-lg text-2xl transition-all ${
                    form.icon === emoji ? 'bg-[#10B981] scale-110' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={form.icon}
              onChange={(e) => setForm({...form, icon: e.target.value})}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-3xl text-center focus:border-[#10B981] transition-all"
              placeholder="Oder eigenes Emoji eingeben..."
            />
          </div>

          <div>
            <label className="text-sm font-bold text-white mb-2 block">Label *</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({...form, label: e.target.value})}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
              placeholder="z.B. Training (Avatar, Vehicle, etc.)"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-white mb-2 block">Beschreibung (optional)</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              placeholder="Optionale Zusatzinfo..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-white mb-2 block">SNAP-Preis * (beliebige Dezimalwerte)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                value={form.snaps}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setForm({...form, snaps: parseFloat(val) || 0.01});
                }}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-xl focus:border-[#10B981] transition-all"
                placeholder="1.0 oder 0.34"
              />
              <p className="text-sm text-[#10B981] mt-2 font-bold">= €{eurPrice}</p>
            </div>
            <div>
              <label className="text-sm font-bold text-white mb-2 block">Sortierung</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-xl focus:border-[#10B981] transition-all"
                placeholder="0"
              />
              <p className="text-xs text-white/40 mt-2">Niedriger = weiter oben</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-white mb-2 block">Farbe</label>
            <div className="flex gap-2">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setForm({...form, color: color.value})}
                  className={`w-12 h-12 rounded-lg border-2 transition ${
                    form.color === color.value ? 'border-white scale-110' : 'border-white/20'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all"
              data-testid="submit-create-item"
            >
              Item erstellen
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
            >
              Abbrechen
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}


// ============================================================================
// PREVIEW SECTION
// ============================================================================

function PreviewSection({ items, pricingSettings }) {
  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-white/50">
        Keine aktiven Items zum Anzeigen
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/30 rounded-2xl">
      
      {/* Header */}
      <h3 className="text-2xl font-bold text-center mb-2">
        💎 SNAP <span className="text-[#10B981]">Preisliste</span>
      </h3>
      <p className="text-center text-white/60 mb-8 text-sm">
        Alle Preise transparent & fair
      </p>
      
      {/* Conversion-Rate */}
      <div className="mb-6 p-4 bg-[#10B981]/10 border border-[#10B981]/40 rounded-xl">
        <div className="flex items-center justify-between">
          <span className="text-white font-bold">1 Euro</span>
          <div className="flex-1 mx-4 border-t-2 border-dashed border-[#10B981]/30"></div>
          <span className="text-[#10B981] font-bold text-xl">{snapsPerEuro} SNAPs</span>
        </div>
      </div>
      
      {/* Items */}
      <div className="space-y-2">
        {items.map((item) => (
          <div 
            key={item.id} 
            className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10"
            style={{ borderColor: `${item.color}20` }}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{item.icon}</span>
              <div>
                <span className="text-white text-sm">{item.label}</span>
                {item.description && <p className="text-white/40 text-xs">{item.description}</p>}
              </div>
            </div>
            <div className="text-right">
              <span className="font-bold" style={{ color: item.color }}>
                {item.snaps} SNAP{item.snaps !== 1 ? 's' : ''}
              </span>
              <p className="text-white/40 text-xs">≈ €{(item.snaps / snapsPerEuro).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>
      
    </div>
  );
}
